import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import api from "../../../../../api/api";
import { deleteInsurance, fetchInsurances } from "../../../../../Redux/insurance/insuranceSlice";
import { getCaseId, getClientId } from "../../../../../Utils/helper";

export const useInsuranceData = () => {
  const dispatch = useDispatch();
  const { data: insurances, modalData } = useSelector((state) => state.insurances);

  const [billData, setBillData] = useState({});
  const [loadingStates, setLoadingStates] = useState({
    isFetchingBills: false,
    isDeleting: false,
  });
  const [errors, setErrors] = useState({
    billError: null,
    deleteError: null,
  });

  const currentCaseId = useMemo(() => getCaseId(), []);
  const clientId = useMemo(() => getClientId(), []);

  const fetchMedicalBill = useCallback(async () => {
    if (!currentCaseId || !clientId) return;

    setLoadingStates((prev) => ({ ...prev, isFetchingBills: true }));
    setErrors((prev) => ({ ...prev, billError: null }));

    try {
      const response = await api.get(
        `/api/settlement-page/lien-information/?case_id=${currentCaseId}&client_id=${clientId}`
      );

      if (response.status === 200 && response?.data?.data) {
        setBillData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching medical bills:", error);
      setErrors((prev) => ({
        ...prev,
        billError: error.message || "Failed to fetch medical bills",
      }));
      setBillData({});
    } finally {
      setLoadingStates((prev) => ({ ...prev, isFetchingBills: false }));
    }
  }, [currentCaseId, clientId]);

  const refreshInsuranceData = useCallback(() => {
    if (clientId && currentCaseId) {
      dispatch(fetchInsurances({ client_id: clientId, case_id: currentCaseId }));
      fetchMedicalBill();
    }
  }, [dispatch, clientId, currentCaseId, fetchMedicalBill]);

  const handleDeleteInsurance = useCallback(
    async (insuranceId) => {
      if (!insuranceId) return;

      setLoadingStates((prev) => ({ ...prev, isDeleting: true }));
      setErrors((prev) => ({ ...prev, deleteError: null }));

      try {
        await dispatch(deleteInsurance({ insurance_id: insuranceId })).unwrap();
        refreshInsuranceData();
        return true;
      } catch (error) {
        console.error("Error deleting insurance:", error);
        setErrors((prev) => ({ ...prev, deleteError: error.message }));
        return false;
      } finally {
        setLoadingStates((prev) => ({ ...prev, isDeleting: false }));
      }
    },
    [dispatch, refreshInsuranceData]
  );

  return {
    insurances,
    modalData,
    billData,
    loadingStates,
    errors,
    currentCaseId,
    clientId,
    fetchMedicalBill,
    refreshInsuranceData,
    handleDeleteInsurance,
  };
};
